import { useEffect, useState } from 'react';

interface NotificationsPanelProps {
  provider: any;
  onActionClick: (action: string) => void;
}

interface Notification {
  id: string;
  status: string;
  title: string;
  message: string;
  ctaText?: string;
  ctaAction?: string;
  icon: string;
  iconColor: string;
  bgColor: string;
  borderColor: string;
  priority: 'high' | 'medium' | 'low';
}

export default function NotificationsPanel({ provider, onActionClick }: NotificationsPanelProps) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    if (provider) {
      generateNotifications();
    }
  }, [provider]);

  const generateNotifications = () => {
    const notifs: Notification[] = [];

    // Determine provider status
    const status = provider.status || 'payment_pending';
    const subscriptionPaid = provider.subscription_paid;
    const idVerified = provider.id_verified;
    const documentsApproved = provider.documents_approved;
    const workwearPurchased = provider.workwear_purchased;
    const isFullyActive = provider.is_active || status === 'fully_active';

    // 1) Payment Pending
    if (!subscriptionPaid && status === 'payment_pending') {
      notifs.push({
        id: 'payment-pending',
        status: 'Payment Pending',
        title: 'Complete your registration',
        message: 'Your account has been created. Complete your subscription to access your dashboard and continue onboarding.',
        ctaText: '👉 Activate account',
        ctaAction: 'payment',
        icon: 'ri-alert-line',
        iconColor: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        priority: 'high'
      });
    }

    // 2) Subscription Paid - ID Verification Required
    if (subscriptionPaid && !idVerified && status === 'registered_verification_pending') {
      notifs.push({
        id: 'id-verification-required',
        status: 'Registered – Verification Pending',
        title: 'Verify your identity',
        message: 'To continue, please complete identity and address verification using our trusted verification partner. Taskbudy does not store your ID documents.',
        ctaText: '👉 Verify identity',
        ctaAction: 'verify-id',
        icon: 'ri-shield-check-line',
        iconColor: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        priority: 'high'
      });
    }

    // 3) ID Verification In Progress
    if (subscriptionPaid && status === 'verification_pending') {
      notifs.push({
        id: 'verification-in-progress',
        status: 'Verification Pending',
        title: 'Verification in progress',
        message: 'Your identity verification is being processed. This usually takes a short time. We\'ll notify you once it\'s complete.',
        icon: 'ri-loader-4-line',
        iconColor: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        priority: 'medium'
      });
    }

    // 4) ID Verified - Documents Required
    if (idVerified && !documentsApproved && status === 'id_verified_documents_pending') {
      notifs.push({
        id: 'documents-required',
        status: 'ID Verified – Documents Pending',
        title: 'Upload required documents',
        message: 'Your identity has been verified. Please upload your insurance and any trade-specific documents to continue.',
        ctaText: '👉 Upload documents',
        ctaAction: 'documents',
        icon: 'ri-file-upload-line',
        iconColor: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        priority: 'high'
      });
    }

    // 5) Documents Under Review
    if (idVerified && status === 'documents_pending_approval') {
      notifs.push({
        id: 'documents-under-review',
        status: 'Documents Pending Approval',
        title: 'Documents under review',
        message: 'Your documents have been submitted and are currently under review. We\'ll notify you once they\'re approved.',
        icon: 'ri-file-search-line',
        iconColor: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        priority: 'medium'
      });
    }

    // 6) Documents Approved - Workwear Required
    if (documentsApproved && !workwearPurchased && status === 'documents_approved_workwear_required') {
      notifs.push({
        id: 'workwear-required',
        status: 'Documents Approved – Workwear Required',
        title: 'Order your Taskbudy workwear',
        message: 'Your documents are approved. To complete activation, please order your Taskbudy starter workwear kit so customers can easily recognise you.',
        ctaText: '👉 Order workwear',
        ctaAction: 'workwear',
        icon: 'ri-shirt-line',
        iconColor: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        priority: 'high'
      });
    }

    // 7) Workwear Ordered - Final Review
    if (workwearPurchased && !isFullyActive && status === 'ready_for_final_review') {
      notifs.push({
        id: 'final-review',
        status: 'Ready for Final Review',
        title: 'Almost there',
        message: 'Your workwear order has been received. We\'re completing a final review of your account before activation.',
        icon: 'ri-time-line',
        iconColor: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        priority: 'medium'
      });
    }

    // 8) Fully Active
    if (isFullyActive) {
      notifs.push({
        id: 'fully-active',
        status: 'Fully Active',
        title: 'You\'re live on Taskbudy 🎉',
        message: 'Your account is now fully active. You can start receiving and accepting job requests.',
        ctaText: '👉 View available jobs',
        ctaAction: 'available-jobs',
        icon: 'ri-checkbox-circle-line',
        iconColor: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        priority: 'low'
      });
    }

    // 9) Incomplete Setup Reminder (if stuck in any incomplete state for too long)
    const createdAt = new Date(provider.created_at);
    const hoursSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60);
    
    if (!isFullyActive && hoursSinceCreation > 24) {
      notifs.push({
        id: 'incomplete-setup',
        status: 'Setup Incomplete',
        title: 'Finish setting up your account',
        message: 'You\'re just a few steps away from becoming fully active. Complete the remaining steps to start receiving jobs.',
        ctaText: '👉 Continue setup',
        ctaAction: 'overview',
        icon: 'ri-error-warning-line',
        iconColor: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        priority: 'medium'
      });
    }

    // 10) Verification Failed (Edge Case)
    if (status === 'verification_failed') {
      notifs.push({
        id: 'verification-failed',
        status: 'Verification Failed',
        title: 'Verification unsuccessful',
        message: 'Unfortunately, we were unable to verify your identity. Please contact our support team for assistance.',
        ctaText: '👉 Contact support',
        ctaAction: 'support',
        icon: 'ri-close-circle-line',
        iconColor: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        priority: 'high'
      });
    }

    // Sort by priority
    notifs.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      return priorityOrder[a.priority] - priorityOrder[b.priority];
    });

    setNotifications(notifs);
  };

  const handleActionClick = (action: string) => {
    onActionClick(action);
  };

  if (notifications.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8 text-center">
        <i className="ri-notification-off-line text-6xl text-gray-300 mb-4"></i>
        <h3 className="text-xl font-bold text-gray-900 mb-2">No notifications</h3>
        <p className="text-gray-600">You're all caught up!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Notifications</h2>
        <p className="text-gray-600 mt-1">Stay updated on your account status and next steps</p>
      </div>

      {notifications.map((notif) => (
        <div
          key={notif.id}
          className={`${notif.bgColor} border-2 ${notif.borderColor} rounded-xl p-6 transition-all duration-300 hover:shadow-md`}
        >
          <div className="flex items-start gap-4">
            <div className={`w-12 h-12 flex items-center justify-center rounded-full ${notif.bgColor} border-2 ${notif.borderColor} flex-shrink-0`}>
              <i className={`${notif.icon} text-2xl ${notif.iconColor} ${notif.icon === 'ri-loader-4-line' ? 'animate-spin' : ''}`}></i>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-4 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${notif.bgColor} ${notif.borderColor} border`}>
                      {notif.status}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{notif.title}</h3>
                </div>
              </div>
              
              <p className="text-gray-700 mb-4 leading-relaxed">{notif.message}</p>
              
              {notif.ctaText && notif.ctaAction && (
                <button
                  onClick={() => handleActionClick(notif.ctaAction)}
                  className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 whitespace-nowrap cursor-pointer transform hover:scale-105 ${
                    notif.priority === 'high'
                      ? 'bg-gradient-to-r from-orange-600 to-red-600 text-white hover:from-orange-700 hover:to-red-700 shadow-lg'
                      : notif.priority === 'medium'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {notif.ctaText}
                </button>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Help Section */}
      <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 mt-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-teal-100 flex-shrink-0">
            <i className="ri-customer-service-2-line text-2xl text-teal-600"></i>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Need help?</h3>
            <p className="text-gray-700 mb-4">
              Our support team is here to assist you with any questions about your account setup or onboarding process.
            </p>
            <button
              onClick={() => handleActionClick('support')}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Support
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
